import type { ReactNode } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { Loader2 } from 'lucide-react'
import { CapaDeModal } from '@/shared/components/CapaDeModal'

interface ConfirmarEliminacionProps {
  titulo: string
  /** Lo que se va a borrar, resaltado dentro del texto. */
  nombre?: string
  /** Lo que conviene que sepa antes de decidir. */
  detalle?: ReactNode
  textoConfirmar?: string
  trabajando?: boolean
  onCancelar: () => void
  onConfirmar: () => void
}

/**
 * Confirmacion de borrado, la misma en todas las pantallas.
 *
 * Existe porque el `window.confirm` del navegador no se puede leer ni vestir:
 * sale con el dominio arriba, dice «says», y congela la pestana completa
 * mientras esta abierto. Ademas cada modulo traia su propia copia de este
 * modal, asi que se veia distinto segun donde estuvieras.
 *
 * El patron sale del modulo de almacen, que es el mas reciente: titulo corto en
 * pregunta, el nombre resaltado dentro de la frase, y el boton de confirmar en
 * rojo con su estado de trabajando.
 */
export const ConfirmarEliminacion = ({
  titulo,
  nombre,
  detalle,
  textoConfirmar = 'Sí, eliminar',
  trabajando = false,
  onCancelar,
  onConfirmar,
}: ConfirmarEliminacionProps) => (
  <CapaDeModal>
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
      <h3 className="text-base font-semibold text-gray-900 mb-2">{titulo}</h3>

      <p className="text-sm text-gray-500 mb-4">
        {nombre && (
          <>
            Esta acción eliminará{' '}
            <span className="font-medium text-gray-700">{nombre}</span>.{' '}
          </>
        )}
        No se puede deshacer.
      </p>

      {detalle && (
        <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 mb-5">
          {detalle}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancelar}
          disabled={trabajando}
          className={estiloDeBoton('secundario')}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={trabajando}
          className={estiloDeBoton('peligro')}
        >
          {trabajando && <Loader2 className="w-4 h-4 animate-spin" />}
          {trabajando ? 'Eliminando…' : textoConfirmar}
        </button>
      </div>
    </div>
  </CapaDeModal>
)
